"use client";

import type { ReactNode } from "react";
import { Users, Wallet } from "lucide-react";
import {
  fillWeeks,
  formatPercent,
  formatUsd,
  hoursForChart,
  percentChange,
  periodLabel,
  recentMondays,
  share,
  weekBarLabel,
  type CustomerInsights,
  type FinancialInsights,
  type PeriodKey,
} from "@/modules/insights/surfaces";
import { DataUnavailable } from "./DataUnavailable";

/**
 * BusinessInsightsScreen (docs/ux/screens/BusinessInsightsScreen.md), shared by
 * both frontends (R22 T14).
 *
 * A clinic's own numbers, scoped to the caller's clinic. **Read-only**: this
 * screen asks the Insights read model and never a command, which is why the
 * whole of it moves with only a `Link` injected.
 *
 * **Two tabs, not one page.** The old version was six flat tiles and two lists,
 * which answered "how many" and almost nothing else: a clinic could read the
 * whole thing and still not know whether work was growing, whether the money
 * came from regulars or from strangers, or how much of the month rested on one
 * practitioner. Customers and Financials are different questions asked by the
 * same person at different moments, and each deserves the whole width.
 *
 * **The tab and the period live in the URL**, so the controls are plain links
 * and the data follows. That also means only the open tab is fetched: two tabs,
 * one query set each, never both.
 *
 * Every number here is derived on read (`04-architecture.md` §3). Nothing on
 * this screen is stored, and nothing it shows can be acted on from here.
 */

export type Tab = "customers" | "financials";

export function parseTab(raw: string | string[] | undefined | null): Tab {
  return raw === "financials" ? "financials" : "customers";
}

/** How this host renders an in-app link. */
export type InsightsLink = (props: {
  href: string;
  className?: string;
  "aria-current"?: "page" | "true" | undefined;
  children: ReactNode;
}) => ReactNode;

export function BusinessInsights({
  tab,
  period,
  businessName,
  customers,
  financials,
  failed,
  configured = true,
  Link,
}: {
  tab: Tab;
  period: PeriodKey;
  businessName: string;
  /** Only the open tab is supplied; the other is null by construction. */
  customers: CustomerInsights | null;
  financials: FinancialInsights | null;
  failed: boolean;
  configured?: boolean;
  Link: InsightsLink;
}) {
  const empty =
    (customers !== null && customers.bookings === 0) ||
    (financials !== null &&
      financials.workCount === 0 &&
      financials.lostCount === 0 &&
      financials.bookedAheadCount === 0);

  return (
    <>
      <Link
        href="/business"
        className="mb-4 inline-block text-sm font-medium text-accent"
      >
        ← Back to your business
      </Link>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-ink">Dashboard</h1>
          <p className="text-sm text-muted">
            {businessName ? `${businessName} · ` : ""}
            Who is coming, and how the business is doing
          </p>
        </div>
        <PeriodToggle active={period} tab={tab} Link={Link} />
      </div>

      <TabBar active={tab} period={period} Link={Link} />

      {failed ? (
        <DataUnavailable configured={configured} what="your dashboard" />
      ) : empty ? (
        <p className="rounded-xl border border-line bg-surface p-8 text-center text-sm text-muted">
          No bookings yet in this period. Once consumers start requesting
          appointments, your numbers show up here.
        </p>
      ) : customers ? (
        <CustomersTab data={customers} />
      ) : financials ? (
        <FinancialsTab data={financials} />
      ) : null}
    </>
  );
}

function TabBar({
  active,
  period,
  Link,
}: {
  active: Tab;
  period: PeriodKey;
  Link: InsightsLink;
}) {
  const tabs = [
    { key: "customers" as const, label: "Customers", Icon: Users },
    { key: "financials" as const, label: "Financials", Icon: Wallet },
  ];
  return (
    <div className="mb-5 inline-flex rounded-lg border border-line bg-surface p-0.5 text-sm">
      {tabs.map(({ key, label, Icon }) => {
        const isActive = key === active;
        return (
          <Link
            key={key}
            href={`/business/insights?tab=${key}&period=${period}`}
            aria-current={isActive ? "true" : undefined}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 font-semibold transition duration-150 ${
              isActive ? "bg-primary text-on-primary" : "text-muted hover:text-ink"
            }`}
          >
            <Icon className="size-4" aria-hidden />
            {label}
          </Link>
        );
      })}
    </div>
  );
}

function PeriodToggle({
  active,
  tab,
  Link,
}: {
  active: PeriodKey;
  tab: Tab;
  Link: InsightsLink;
}) {
  const options: PeriodKey[] = ["all", "30d"];
  return (
    <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-sm">
      {options.map((option) => {
        const isActive = option === active;
        return (
          <Link
            key={option}
            href={`/business/insights?tab=${tab}&period=${option}`}
            aria-current={isActive ? "true" : undefined}
            className={`rounded-md px-3 py-1.5 font-medium ${
              isActive ? "bg-primary text-on-primary" : "text-muted hover:text-ink"
            }`}
          >
            {periodLabel(option)}
          </Link>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------- customers -- */

function CustomersTab({ data }: { data: CustomerInsights }) {
  // The anchor is the server's today; the week maths itself is pure and tested.
  const mondays = recentMondays(new Date().toISOString().slice(0, 10), 4);
  const weeks = fillWeeks(data.weekly, mondays);
  const peakWeek = Math.max(1, ...weeks.map((w) => w.count));
  const thisWeek = weeks[weeks.length - 1].count;
  const lastWeek = weeks[weeks.length - 2].count;
  const delta = percentChange(thisWeek, lastWeek);

  const hours = hoursForChart(data.byHour);
  const peakHour = Math.max(1, ...hours.map((h) => h.count));
  const serviceTotal = data.serviceShare.reduce((n, s) => n + s.count, 0);

  return (
    <div className="space-y-5">
      {/* Is work coming in? */}
      <Panel>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-muted">Appointments this week</p>
            <p className="mt-1 flex items-baseline gap-2">
              <span className="font-mono text-4xl font-bold text-ink">{thisWeek}</span>
              {delta !== null && (
                <span
                  className={`text-sm font-semibold ${delta >= 0 ? "text-accent" : "text-danger"}`}
                >
                  {delta >= 0 ? "+" : ""}
                  {delta}% on last week
                </span>
              )}
            </p>
          </div>
          <div className="flex h-20 items-end gap-2">
            {weeks.map((w, i) => (
              <div key={w.weekStart} className="flex w-11 flex-col items-center gap-1.5">
                <span
                  className={`w-full rounded-[3px] ${
                    i === weeks.length - 1 ? "bg-accent" : "bg-accent/25"
                  }`}
                  style={{ height: `${(w.count / peakWeek) * 56 + 4}px` }}
                />
                <span className="font-mono text-[10px] text-muted">
                  {weekBarLabel(w.weekStart)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      {/* Am I keeping people? */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Tile
          label="Customers"
          value={String(data.people)}
          hint="People who booked in this period"
        />
        <Tile
          label="Returning"
          value={formatPercent(data.returning.count, data.returning.confirmingConsumers)}
          hint={`${data.returning.count} of ${data.returning.confirmingConsumers} came back`}
        />
        <Tile
          label="Requests accepted"
          value={formatPercent(data.accepted, data.decided)}
          hint={`${data.accepted} of ${data.decided} decided`}
        />
      </div>

      {/* How the clinic behaves, seen from the customer's side. */}
      <Panel>
        <h2 className="font-bold text-ink">How it feels to book you</h2>
        <p className="mt-0.5 text-sm text-muted">
          What the waiting costs you, from the customer&apos;s side of it
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Metric
            value={formatPercent(data.droppedByConsumer, data.bookings)}
            label="Dropped by the customer"
            note="Withdrawn or cancelled after requesting. A high number here usually means people waited too long to hear back"
          />
          <Metric
            value={String(data.bookings - data.decided)}
            label="Still waiting for an answer"
            note="Requests you have not decided yet. Every one of them is someone deciding whether to call another clinic"
          />
        </div>
        <p className="mt-4 rounded-lg border border-line bg-page px-3 py-2 text-xs text-muted">
          How <em>long</em> people wait for an answer is the number worth acting
          on, and it is not here yet: it needs the moment a request was decided
          to be recorded. See the handoff notes, backend §5g.
        </p>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* What do they come for? */}
        <Panel>
          <h2 className="font-bold text-ink">What they book</h2>
          <p className="mt-0.5 text-sm text-muted">Share of all appointments</p>
          {data.serviceShare.length === 0 ? (
            <p className="mt-4 text-sm text-muted">No bookings in this period.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {data.serviceShare.map((s) => (
                <li key={s.serviceName}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="truncate font-medium text-ink">{s.serviceName}</span>
                    <span className="shrink-0 font-mono text-muted">
                      {share(s.count, serviceTotal)}%
                    </span>
                  </div>
                  <Bar value={share(s.count, serviceTotal)} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* When do they want it? */}
        <Panel>
          <h2 className="font-bold text-ink">When they want it</h2>
          <p className="mt-0.5 text-sm text-muted">Appointments by hour of the day</p>
          <div className="mt-4 flex items-end gap-1.5">
            {hours.map((h) => (
              <div key={h.hour} className="flex flex-1 flex-col items-center gap-1.5">
                <span
                  className={`w-full rounded-[3px] ${h.count === 0 ? "bg-line" : "bg-accent"}`}
                  style={{
                    height: `${(h.count / peakHour) * 72 + 4}px`,
                    opacity: h.count === 0 ? 1 : 0.35 + (h.count / peakHour) * 0.65,
                  }}
                />
                <span className="font-mono text-[10px] text-muted">
                  {String(h.hour).padStart(2, "0")}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-muted">
            Darker is busier. Useful for deciding when to add a practitioner
          </p>
        </Panel>
      </div>

      {/* Who, specifically */}
      <Panel>
        <h2 className="font-bold text-ink">Your regulars</h2>
        <p className="mt-0.5 text-sm text-muted">
          Most appointments in this period. Counted by account, not by the name
          they gave
        </p>
        {data.regulars.length === 0 ? (
          <p className="mt-4 text-sm text-muted">Nobody has booked yet.</p>
        ) : (
          <ul className="mt-4 divide-y divide-line">
            {data.regulars.map((r) => (
              <li
                key={r.consumerId}
                className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
              >
                <span
                  className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent/10 font-semibold text-accent"
                  aria-hidden
                >
                  {r.consumerName.charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1 truncate font-semibold text-ink">
                  {r.consumerName}
                </span>
                <span className="shrink-0 font-mono text-sm font-semibold text-ink">
                  {r.visits} {r.visits === 1 ? "visit" : "visits"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------ financials -- */

function FinancialsTab({ data }: { data: FinancialInsights }) {
  const monthDelta = percentChange(data.earnedThisMonthCents, data.earnedLastMonthCents);
  const averageCents = data.workCount
    ? Math.round(data.workRevenueCents / data.workCount)
    : 0;
  const perCustomerCents = data.workCustomers
    ? Math.round(data.workRevenueCents / data.workCustomers)
    : 0;
  const repeatShare = share(data.repeatRevenueCents, data.workRevenueCents);

  const concentration = [
    { row: data.topPractitioner, kind: "of income is one practitioner" },
    { row: data.topService, kind: "of income is one service" },
  ].filter((c): c is { row: NonNullable<typeof c.row>; kind: string } => c.row !== null);

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Earned this month"
          value={formatUsd(data.earnedThisMonthCents)}
          hint={
            monthDelta === null
              ? `${data.completedThisMonth} completed`
              : `${monthDelta >= 0 ? "+" : ""}${monthDelta}% on ${formatUsd(data.earnedLastMonthCents)} last month`
          }
        />
        <Tile
          label="Booked ahead"
          value={formatUsd(data.bookedAheadCents)}
          hint={`${data.bookedAheadCount} confirmed, not yet happened`}
        />
        <Tile
          label="Average appointment"
          value={formatUsd(averageCents)}
          hint={`Across ${data.workCount} confirmed`}
        />
        <Tile
          label="Revenue per customer"
          value={formatUsd(perCustomerCents)}
          hint={`Across ${data.workCustomers} ${data.workCustomers === 1 ? "person" : "people"}`}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Retention, priced. */}
        <Panel>
          <h2 className="font-bold text-ink">Regulars versus first-timers</h2>
          <p className="mt-0.5 text-sm text-muted">
            Where the money comes from, not how many people it came from
          </p>
          <p className="mt-4 flex items-baseline gap-3">
            <span className="font-mono text-3xl font-bold text-ink">{repeatShare}%</span>
            <span className="text-sm text-muted">from people who came back</span>
          </p>
          <Bar value={repeatShare} />
          <p className="mt-3 text-sm text-muted">
            {repeatShare >= 50
              ? "Most of the income is repeat work, which is the cheaper half. Protect it."
              : "Most of the income is first visits, which have to be won again every month. The lever is getting people to book a second time."}
          </p>
        </Panel>

        {/* Concentration risk. */}
        <Panel>
          <h2 className="font-bold text-ink">How much rests on one thing</h2>
          <p className="mt-0.5 text-sm text-muted">
            What happens to the month if this practitioner, or this service,
            stops
          </p>
          {concentration.length === 0 ? (
            <p className="mt-4 text-sm text-muted">No confirmed work in this period.</p>
          ) : (
            <ul className="mt-4 space-y-4">
              {concentration.map(({ row, kind }) => {
                const pct = share(row.totalCents, data.workRevenueCents);
                return (
                  <li key={kind}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="truncate font-semibold text-ink">{row.key}</span>
                      <span
                        className={`shrink-0 font-mono font-bold ${
                          pct >= 50 ? "text-danger" : "text-ink"
                        }`}
                      >
                        {pct}%
                      </span>
                    </div>
                    <Bar value={pct} tone={pct >= 50 ? "danger" : "accent"} />
                    <p className="mt-1 text-xs text-muted">{kind}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>

      {/* The number nobody puts on a dashboard, and the one worth acting on. */}
      <Panel>
        <h2 className="font-bold text-ink">What did not happen</h2>
        <p className="mt-0.5 text-sm text-muted">
          Requests declined, expired or cancelled, at the price they would have
          been
        </p>
        <p className="mt-4 flex items-baseline gap-3">
          <span className="font-mono text-3xl font-bold text-ink">
            {formatUsd(data.lostCents)}
          </span>
          <span className="text-sm text-muted">
            across {data.lostCount} {data.lostCount === 1 ? "booking" : "bookings"}
          </span>
        </p>
        <p className="mt-3 text-sm text-muted">
          Some of this is unavoidable. The part that is not is usually the
          requests that waited too long for an answer, which is the Customers
          tab.
        </p>
      </Panel>
    </div>
  );
}

/* ----------------------------------------------------------------- bits -- */

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
      {children}
    </div>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4 shadow-sm">
      <p className="text-sm font-medium text-muted">{label}</p>
      <p className="mt-1 font-mono text-2xl font-bold text-ink">{value}</p>
      <p className="mt-1 text-xs text-muted">{hint}</p>
    </div>
  );
}

function Metric({
  value,
  label,
  note,
}: {
  value: string;
  label: string;
  note: string;
}) {
  return (
    <div>
      <p className="font-mono text-2xl font-bold text-ink">{value}</p>
      <p className="mt-0.5 text-sm font-medium text-ink">{label}</p>
      <p className="mt-0.5 text-xs text-muted">{note}</p>
    </div>
  );
}

function Bar({ value, tone = "accent" }: { value: number; tone?: "accent" | "danger" }) {
  return (
    <div className="mt-1.5 h-2 rounded-[2px] bg-line">
      <div
        className={`h-2 rounded-[2px] ${tone === "danger" ? "bg-danger" : "bg-accent"}`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}
